import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, Divider, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Select, FormControl, InputLabel, IconButton, CircularProgress,
  Stepper, Step, StepLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import RestoreIcon from '@mui/icons-material/Restore';
import { toast } from 'react-toastify';
import BrowserTitle from '../components/Helmet';
import { AuthContext } from '../context/AuthProvider';
import warrantyTicketServices from '../services/warrantyTicketServices';
import { stringifyAddress } from '../utils/formatter';
import WarrantyTicketNextActionBanner from '../components/WarrantyTicketNextActionBanner';
import WarrantyTicketPhotoSection from '../components/WarrantyTicketPhotoSection';
import ScaFormDialog from '../components/ScaFormDialog';

const priorityColor = { High: '#d32f2f', Medium: '#ed6c02', Low: '#2e7d32' };
const statusColor = { Active: 'primary', 'In Progress': 'warning', Resolved: 'success' };
const STATUS_STEPS = ['Active', 'In Progress', 'Resolved'];

export default function WarrantyTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scaDialogOpen, setScaDialogOpen] = useState(false);

  const isAdmin = userData?.role === 'superadmin' || userData?.role === 'admin';
  const isDispatcher = userData?.role === 'dispatcher';
  const isTechnician = userData?.role === 'technician';
  const canEdit = isAdmin || (isTechnician && ticket?.assignedTech?.id === userData?.id);
  const canDelete = isAdmin;
  const isReadOnly = isDispatcher;

  const fetchTicket = async () => {
    setLoading(true);
    try {
      const res = await warrantyTicketServices.getById(id);
      if (res.status === 200) {
        setTicket(res.data);
      }
    } catch (e) {
      toast.error('Error loading ticket');
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleStatusChange = async () => {
    try {
      const res = await warrantyTicketServices.changeStatus(id, newStatus);
      if (res.status === 200) {
        setTicket(res.data);
        toast.success(`Status changed to ${newStatus}`);
      }
    } catch (e) {
      toast.error('Error changing status');
    }
    setStatusDialogOpen(false);
  };

  const handleResolve = async () => {
    try {
      const res = await warrantyTicketServices.resolve(id, completionNotes);
      if (res.status === 200) {
        setTicket(res.data);
        toast.success('Ticket resolved');
      }
    } catch (e) {
      toast.error('Error resolving ticket');
    }
    setResolveOpen(false);
    setCompletionNotes('');
  };

  const handleDelete = async () => {
    try {
      await warrantyTicketServices.remove(id);
      toast.success('Ticket deleted');
      navigate('/warranty-tickets');
    } catch (e) {
      toast.error('Error deleting ticket');
    }
    setDeleteDialogOpen(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">Ticket not found</Typography>
        <Button onClick={() => navigate('/warranty-tickets')} sx={{ mt: 2 }}>Back to list</Button>
      </Box>
    );
  }

  const activeStep = STATUS_STEPS.indexOf(ticket.status);

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <BrowserTitle title={`Ticket ${ticket.ticketNumber}`} />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/warranty-tickets')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
          {ticket.ticketNumber}
        </Typography>
        <Chip
          label={ticket.status}
          color={statusColor[ticket.status] || 'default'}
          sx={{ fontWeight: 700 }}
        />
        <Chip
          label={ticket.priority}
          sx={{
            backgroundColor: priorityColor[ticket.priority],
            color: '#fff',
            fontWeight: 700,
          }}
        />
      </Box>

      {/* Status Stepper */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STATUS_STEPS.map((label) => (
            <Step key={label} completed={STATUS_STEPS.indexOf(label) <= activeStep}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <WarrantyTicketNextActionBanner ticket={ticket} sx={{ mb: 2 }} />

      {/* Actions */}
      {!isReadOnly && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {canEdit && ticket.status !== 'Resolved' && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  setNewStatus(ticket.status === 'Active' ? 'In Progress' : 'Resolved');
                  setStatusDialogOpen(true);
                }}
              >
                Change Status
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => setResolveOpen(true)}
              >
                Mark as Resolved
              </Button>
            </>
          )}
          {isAdmin && ticket.status === 'Resolved' && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RestoreIcon />}
              onClick={() => {
                setNewStatus('Active');
                setStatusDialogOpen(true);
              }}
            >
              Reopen Ticket
            </Button>
          )}
          {canEdit && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<DescriptionIcon />}
              onClick={() => setScaDialogOpen(true)}
            >
              Sigler SCA Claim
            </Button>
          )}
          {canDelete && ticket.status !== 'Resolved' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          )}
        </Box>
      )}

      {/* Customer & Address */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Customer & Address</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Address</Typography>
            <Typography>{ticket.property ? stringifyAddress(ticket.property) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Customer</Typography>
            <Typography>
              {ticket.client
                ? `${ticket.client.firstName || ''} ${ticket.client.lastName || ''}`.trim()
                : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Phone</Typography>
            <Typography>{ticket.customerPhone || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Proposal</Typography>
            <Typography>
              {ticket.notAirmaxxInstallation
                ? 'Not Airmaxx Installation'
                : (ticket.proposal?.publicId || ticket.legacyProposalNumber || '—')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Created By (Assigned Tech)</Typography>
            <Typography>
              {ticket.createdBy
                ? `${ticket.createdBy.firstName || ''} ${ticket.createdBy.lastName || ''}`.trim()
                : ticket.assignedTech
                  ? `${ticket.assignedTech.firstName || ''} ${ticket.assignedTech.lastName || ''}`.trim()
                  : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Created</Typography>
            <Typography>{formatDateTime(ticket.createdAt)}</Typography>
          </Box>
          {ticket.legacyProposalNumber && (
            <Box>
              <Typography variant="caption" color="text.secondary">Legacy Proposal</Typography>
              <Typography>{ticket.legacyProposalNumber}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* System & Issue */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>System & Issue</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Installation Year</Typography>
            <Typography>{ticket.installationYear || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Brand</Typography>
            <Typography>{ticket.brandModel || '—'}</Typography>
          </Box>
          {(ticket.defectiveModel || ticket.defectiveSerial) && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">Defective Equipment Model</Typography>
                <Typography>{ticket.defectiveModel || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Defective Equipment Serial #</Typography>
                <Typography>{ticket.defectiveSerial || '—'}</Typography>
              </Box>
            </>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">Work Status</Typography>
            <Typography>{ticket.workStatus || '—'}</Typography>
          </Box>
          {ticket.resolvedAt && (
            <Box>
              <Typography variant="caption" color="text.secondary">Resolved</Typography>
              <Typography>{formatDateTime(ticket.resolvedAt)}</Typography>
            </Box>
          )}
        </Box>
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary">Issue Description</Typography>
        <Typography sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{ticket.issueDescription}</Typography>

        {ticket.completionNotes && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary">Completion Notes</Typography>
            <Typography sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{ticket.completionNotes}</Typography>
          </>
        )}
      </Paper>

      <WarrantyTicketPhotoSection ticket={ticket} canEdit={canEdit} />

      {/* Follow-up */}
      {(ticket.followUpDate || ticket.followUpReason) && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Follow-up</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Date</Typography>
              <Typography>{formatDate(ticket.followUpDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Time</Typography>
              <Typography>{ticket.followUpTime || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Reason</Typography>
              <Typography>{ticket.followUpReason || '—'}</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Parts */}
      {ticket.parts && ticket.parts.length > 0 && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Parts ({ticket.parts.length})
          </Typography>
          {ticket.parts.map((part, i) => (
            <Box
              key={part.id}
              sx={{
                p: 2, mb: i < ticket.parts.length - 1 ? 1 : 0,
                bgcolor: 'action.hover', borderRadius: 1
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>{part.partDescription}</Typography>
              <Box sx={{ display: 'flex', gap: 3, mt: 0.5, flexWrap: 'wrap' }}>
                {part.orderedFrom && (
                  <Typography variant="body2" color="text.secondary">
                    {part.orderedFrom === 'Ordered by Phone' ? 'Ordered by Phone' : `From: ${part.orderedFrom}`}
                  </Typography>
                )}
                {part.poNumber && (
                  <Typography variant="body2" color="text.secondary">
                    PO: {part.poNumber}
                  </Typography>
                )}
                {part.orderDate && (
                  <Typography variant="body2" color="text.secondary">
                    Ordered: {formatDate(part.orderDate)}
                  </Typography>
                )}
                {part.expectedArrival && (
                  <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                    ETA: {formatDate(part.expectedArrival)}
                  </Typography>
                )}
              </Box>
              {(part.failedPartNumber || part.failedPartSerial) && (
                <Typography variant="body2" color="error.main" sx={{ mt: 0.5, fontWeight: 600 }}>
                  Failed part: {part.failedPartNumber || '—'}{part.failedPartSerial ? ` (SN: ${part.failedPartSerial})` : ''}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}

      {/* Activity Log */}
      {ticket.activities && ticket.activities.length > 0 && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Activity Log</Typography>
          {ticket.activities.map((activity) => (
            <Box
              key={activity.id}
              sx={{ display: 'flex', gap: 2, py: 1, borderBottom: '1px solid #f0f0f0' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {activity.action.replace(/_/g, ' ')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {activity.description}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatDateTime(activity.createdAt)}
                </Typography>
                {activity.user && (
                  <Typography variant="caption" color="text.secondary">
                    {activity.user.firstName} {activity.user.lastName}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      {/* Resolve Dialog */}
      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as Resolved</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will set the ticket status to Resolved and record the resolution timestamp.
          </Typography>
          <TextField
            label="Completion Notes (optional)"
            multiline
            rows={4}
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleResolve}>
            Resolve Ticket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Status Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>New Status</InputLabel>
            <Select value={newStatus} label="New Status" onChange={(e) => setNewStatus(e.target.value)}>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Resolved">Resolved</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusChange}>
            Change Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Ticket</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete ticket {ticket.ticketNumber}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sigler SCA Claim Dialog */}
      <ScaFormDialog
        open={scaDialogOpen}
        onClose={(sent) => {
          setScaDialogOpen(false);
          if (sent) fetchTicket();
        }}
        ticket={ticket}
      />
    </Box>
  );
}
